import createActions from "helpers/actionCreator"
import { createReducer } from "reduxsauce"
import { call, put, takeLatest, select } from "redux-saga/effects"
import { chain, get, includes, pick } from "lodash"
import { DESC } from "helpers/sorts"
import { Types as LoaderTypes } from "model/data"
import { Types as GQLLoaderTypes } from "model/graphql"
import { assetClickHandler, getTracking } from "helpers/assets"
import { getSegment } from "modules/asset-analytics/selectors"
import { getSelectedGroup } from "./selectors"

export const { Types, Creators } = createActions("ompnt/modules/asset-analytics-table", {
  hideGroup: [],
  itemClick: {
    props: ["screen", "category", "item", "column", "contentSetId"],
    ...getTracking("assets"),
  },
  setMetricType: {
    props: ["metricType"],
    track: true,
    trackType: "asset-metric-type-selected",
    trackPayload: ({ metricType }) => ({ metricType }),
  },
  setColumnOrder: {
    props: ["columnOrder"],
    track: true,
    trackType: "asset-column-order-selected",
    trackPayload: ({ columnOrder }) => ({ columnOrder }),
  },
  setPage: {
    props: ["page"],
    track: true,
    trackType: "asset-page-updated",
    trackPayload: ({ page }) => ({ page }),
  },
  setSelectedGroup: {
    props: ["id"],
    track: true,
    trackType: "asset-grouping-selected",
    trackPayload: ({ id }) => ({ id }),
  },
  setSelectedSubView: {
    props: ["subView"],
    track: true,
    trackType: "asset-sub-view-selected",
    trackPayload: ({ subView }) => ({ subView }),
  },
  setSelectedView: {
    props: ["id"],
    track: true,
    trackType: "asset-view-selected",
    trackPayload: ({ id }) => ({ id }),
  },
  setSort: {
    props: ["sort"],
    track: true,
    trackType: "asset-sort-updated",
    trackPayload: ({ sort }) => pick(sort, ["type", "id", "direction"]),
  },
})

const DEFAULT_STATE = {
  columnOrder: "default",
  page: 0,
  selectedGroup: null,
  selectedMetricType: null,
  selectedSubView: null,
  sort: {
    id: "percentEquity",
    direction: DESC,
  },
}

function handleHideGroup(state) {
  return { ...state, selectedGroup: null }
}

function handleLoadSuccess(state, action) {
  if (action.resource === "portfolio") {
    return { ...DEFAULT_STATE }
  }
  if (includes(["assetAnalyticsComposition", "assetAnalyticsGroupedComposition"], action.resource)) {
    return { ...state, page: 0 }
  }

  return state
}

function handleSetPage(state, { page }) {
  return { ...state, page }
}

function handleSetSelectedGroup(state, { id }) {
  return { ...state, selectedGroup: id }
}

function handleSetSelectedViewId(state) {
  return { ...state,
    selectedMetricType: null,
    selectedSubView: null,
    selectedGroup: null,
  }
}

function handleSetMetricType(state, { metricType }) {
  return { ...state, selectedMetricType: metricType }
}

function handleSetColumnOrder(state, { columnOrder }) {
  return { ...state, columnOrder: columnOrder }
}

function handleSetSelectedSubView(state, { subView }) {
  return { ...state, selectedSubView: subView }
}

function handleSetSort(state, { sort }) {
  return { ...state, sort }
}

export default createReducer(DEFAULT_STATE, {
  [Types.HIDE_GROUP]: handleHideGroup,
  [Types.SET_METRIC_TYPE]: handleSetMetricType,
  [Types.SET_COLUMN_ORDER]: handleSetColumnOrder,
  [Types.SET_SELECTED_SUB_VIEW]: handleSetSelectedSubView,
  [Types.SET_PAGE]: handleSetPage,
  [Types.SET_SELECTED_GROUP]: handleSetSelectedGroup,
  [Types.SET_SELECTED_VIEW]: handleSetSelectedViewId,
  [Types.SET_SORT]: handleSetSort,
  [LoaderTypes.LOAD_SUCCESS]: handleLoadSuccess,
  [GQLLoaderTypes.LOAD_SUCCESS]: handleLoadSuccess,
})

function* handleItemClick(params) {
  const stateParams = yield select(state => ({
    account: get(state, "global.selected.account"),
    portfolio: get(state, "global.data.portfolio.data"),
    currentPeriod: get(state, "modules.assetAnalytics.period"),
    currentGrouping: get(state, "modules.assetAnalytics.grouping"),
    segment: getSegment(state) || {},
    // Cannot reuse the selector defined in ./selectors because it would create a circular dependency
    groups: get(state, "global.data.assetAnalyticsCompositionGrouped.data.model.simulation.composition.0.compositionBy"),
  }))

  yield call(assetClickHandler, { ...params, ...stateParams })
}

function* handleGQLLoadSuccess({ resource, data }) {
  const { selectedGroup } = yield select(state => ({
    selectedGroup: getSelectedGroup(state),
  }))

  if (resource === "assetAnalyticsGroupedComposition") {
    if (selectedGroup) {
      const selectedGroupExists = chain(data)
        .get("model.simulation.composition.0.compositionBy", [])
        .some(group => group.id === selectedGroup)
        .value()

      if (!selectedGroupExists) {
        yield put(Creators.hideGroup())
      }
    }
  } else if (resource === "assetAnalyticsComposition") {
    if (selectedGroup) {
      yield put(Creators.hideGroup())
    }
  }
}

export function* saga() {
  yield takeLatest(Types.ITEM_CLICK, handleItemClick)
  yield takeLatest(GQLLoaderTypes.LOAD_SUCCESS, handleGQLLoadSuccess)
}
